import { TimeBoundsSource } from "@/shared/state/visualization/schema";

import {
  formatTimeByMode,
  useTimeDisplayMode,
  useWorkspaceTimeBounds,
} from "../../WorkspaceCtx";

import type { ToolHover } from "./BabylonManager";
import styles from "./CoordinateTooltip.module.css";
import { formatMeters } from "./formatMeters";

/** How far the tooltip sits from the cursor, so the crosshair stays visible. */
const CURSOR_OFFSET_PX = 14;

/**
 * Assumed footprint of the readout, for choosing which side of the cursor
 * it goes on. Constants rather than a measurement of the rendered box,
 * which would cost a layout read per cursor move. The base size covers
 * UTM-scale `500000.000` on all three lines; a timestamp line is both
 * taller and wider (a Local-mode time runs to ~28 monospace characters),
 * so a timestamped hover assumes the larger footprint on both axes.
 */
const TOOLTIP_WIDTH_PX = 150;
const TIMESTAMPED_TOOLTIP_WIDTH_PX = 230;
const TOOLTIP_HEIGHT_PX = 72;
const TIME_LINE_HEIGHT_PX = 18;

interface CoordinateTooltipProps {
  /** What the measure tool is over; `null` renders nothing. */
  hover: ToolHover | null;
  /**
   * The box the tooltip is positioned in, so it can flip to the other side
   * of the cursor rather than run off an edge. A zero width or height means
   * the box is not yet measured; the tooltip stays on its default side.
   */
  bounds: { width: number; height: number };
}

/**
 * Cursor-following readout of the data-space coordinates under the measure
 * tool's crosshair. A DOM overlay rather than in-scene text, so it inherits
 * the app's typography and theme tokens. `hover.screenX/Y` are
 * canvas-relative, so the caller must render it inside the same positioned
 * box as the canvas.
 *
 * Sits down-right of the cursor, flipping up and/or left near the far edges
 * of that box so the readout is never clipped mid-number.
 *
 * A pick that landed on a trajectory sample also reads out when that sample
 * was logged, in the workspace's time display mode — the same mode and
 * formatter the timeline and the plot and log panels use.
 */
export function CoordinateTooltip({ hover, bounds }: CoordinateTooltipProps) {
  const [timeDisplayMode] = useTimeDisplayMode();
  const timeBounds = useWorkspaceTimeBounds({
    timeBoundsSource: TimeBoundsSource.FilesInWorkspace,
  });

  if (!hover) {
    return null;
  }
  const timestamped = hover.sampleTime !== undefined;
  const tooltipHeight = timestamped
    ? TOOLTIP_HEIGHT_PX + TIME_LINE_HEIGHT_PX
    : TOOLTIP_HEIGHT_PX;
  const tooltipWidth = timestamped
    ? TIMESTAMPED_TOOLTIP_WIDTH_PX
    : TOOLTIP_WIDTH_PX;
  const flipLeft =
    bounds.width > 0 &&
    hover.screenX + CURSOR_OFFSET_PX + tooltipWidth > bounds.width;
  const flipUp =
    bounds.height > 0 &&
    hover.screenY + CURSOR_OFFSET_PX + tooltipHeight > bounds.height;
  return (
    <div
      className={styles.tooltip}
      role="status"
      style={{
        left: hover.screenX + (flipLeft ? -CURSOR_OFFSET_PX : CURSOR_OFFSET_PX),
        top: hover.screenY + (flipUp ? -CURSOR_OFFSET_PX : CURSOR_OFFSET_PX),
        // Anchored by the corner nearest the cursor, so the flip moves the
        // box rather than the point it is reporting.
        transform: `translate(${flipLeft ? "-100%" : "0%"}, ${flipUp ? "-100%" : "0%"})`,
      }}
    >
      <span className={styles.axis}>x</span> {formatMeters(hover.dataPoint.x)}
      <br />
      <span className={styles.axis}>y</span> {formatMeters(hover.dataPoint.y)}
      <br />
      <span className={styles.axis}>z</span> {formatMeters(hover.dataPoint.z)}
      {hover.sampleTime !== undefined && (
        <>
          <br />
          <span className={styles.axis}>t</span>{" "}
          {formatTimeByMode(
            hover.sampleTime,
            timeDisplayMode,
            timeBounds.earliest,
            false,
          )}
        </>
      )}
    </div>
  );
}
